import CookieNameSpace from '~/const/cookie';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import type { IProfileUser } from '~~/shared/interfaces/profile';
import { clearOfflineRequests } from '~/helpers/offlineRequestQueue';
import {
	clearTruckerBackgroundCache,
	configureTruckerBackgroundRunner
} from '~/helpers/truckerBackgroundCache';

const PROFILE_CACHE_KEY = 'cached_profile';

export const normalizeProfile = (value: unknown): IProfileUser | null => {
	let candidate = value;
	while (
		candidate &&
		typeof candidate === 'object' &&
		'data' in candidate &&
		candidate.data &&
		typeof candidate.data === 'object'
	) {
		candidate = candidate.data;
	}

	if (!candidate || typeof candidate !== 'object' || !('role' in candidate) || typeof candidate.role !== 'string') {
		return null;
	}

	const profileId =
		'Id' in candidate ? candidate.Id : 'id' in candidate ? candidate.id : undefined;
	if (profileId !== undefined && typeof profileId !== 'string' && typeof profileId !== 'number') return null;

	return {
		...candidate,
		...(profileId !== undefined && { Id: String(profileId) })
	} as IProfileUser;
};

export const getCachedProfile = async (): Promise<IProfileUser | null> => {
	if (import.meta.server) return null;

	const cachedValues: string[] = [];
	if (Capacitor.isNativePlatform()) {
		try {
			const value = (await Preferences.get({ key: PROFILE_CACHE_KEY })).value;
			if (value) cachedValues.push(value);
		} catch (error) {
			console.error('Failed to read cached profile from Preferences:', error);
		}
	}
	try {
		const value = localStorage.getItem(PROFILE_CACHE_KEY);
		if (value) cachedValues.push(value);
	} catch (error) {
		console.error('Failed to read cached profile from local storage:', error);
	}

	for (const value of cachedValues) {
		try {
			const profile = normalizeProfile(JSON.parse(value));
			if (profile) return profile;
			console.error('Cached profile has an invalid format.');
		} catch (error) {
			console.error('Failed to parse cached profile:', error);
		}
	}
	return null;
};

export const saveCachedProfile = async (profile: IProfileUser) => {
	if (import.meta.server) return;

	const normalizedProfile = normalizeProfile(profile);
	if (!normalizedProfile) throw new Error('Cannot cache profile: the response has no profile ID or role.');

	const value = JSON.stringify(normalizedProfile);
	if (Capacitor.isNativePlatform()) {
		try {
			await Preferences.set({ key: PROFILE_CACHE_KEY, value });
		} catch (error) {
			console.error('Failed to persist cached profile in Preferences:', error);
		}
	}

	try {
		localStorage.setItem(PROFILE_CACHE_KEY, value);
	} catch (error) {
		if (!Capacitor.isNativePlatform()) throw error;
		console.error('Failed to persist cached profile in local storage:', error);
		const cachedProfile = (await Preferences.get({ key: PROFILE_CACHE_KEY })).value;
		if (!cachedProfile) throw error;
	}
};

export const removeCachedProfile = async () => {
	if (import.meta.server) return;

	if (Capacitor.isNativePlatform()) {
		await Preferences.remove({ key: PROFILE_CACHE_KEY });
	}

	localStorage.removeItem(PROFILE_CACHE_KEY);
};

// token methods
const getExpirationTimestamp = (validTo?: Date | string | number) => {
	if (validTo === undefined) return null;

	const timestamp =
		validTo instanceof Date
			? validTo.getTime()
			: typeof validTo === 'number' || /^\d+(\.\d+)?$/.test(validTo)
				? Number(validTo)
				: new Date(validTo).getTime();
	if (!Number.isFinite(timestamp)) return null;
	return timestamp < 1_000_000_000_000 ? timestamp * 1000 : timestamp;
};

export const addTokenInStorage = async (key: string, value: string, validTo?: Date | string | number) => {
	const runtimeConfig = useRuntimeConfig();
	const { clearAuthToken } = useAuthToken();
	clearAuthToken();
	const expiresAt = getExpirationTimestamp(validTo);

	if (runtimeConfig.public.tokenStorage === 'preferenceStorage') {
		await Preferences.set({ key, value });
		useCookie(CookieNameSpace.getAccessToken).value = 'preferenceStorage';
		if (expiresAt) await setExpiredToken(expiresAt);
		await configureTruckerBackgroundRunner(String(runtimeConfig.public.baseURL || ''), value);
		return;
	}

	if (runtimeConfig.public.tokenStorage === 'localStorage') {
		localStorage.setItem(key, value);
		useCookie(CookieNameSpace.getAccessToken).value = 'tokenInLocalStorage';
		if (expiresAt) setExpiredToken(expiresAt);
		await configureTruckerBackgroundRunner(String(runtimeConfig.public.baseURL || ''), value);
		return;
	}

	useCookie(key, { path: '/' }).value = value;
	if (expiresAt) setExpiredToken(expiresAt);
	await configureTruckerBackgroundRunner(String(runtimeConfig.public.baseURL || ''), value);
};

const setExpiredToken = async (expiresAt: number) => {
	const runtimeConfig = useRuntimeConfig();

	if (runtimeConfig.public.tokenStorage === 'preferenceStorage') {
		await Preferences.set({
			key: CookieNameSpace.tokenExpired,
			value: `${expiresAt}`
		});
		return;
	}

	useCookie(CookieNameSpace.tokenExpired).value = `${expiresAt}`;
};

export const checkForNeedUpdateToken = async () => {
	const runtimeConfig = useRuntimeConfig();
	const currentTime = new Date().getTime();

	let StorageTime = 0;

	if (runtimeConfig.public.tokenStorage === 'preferenceStorage') {
		const { value } = await Preferences.get({ key: CookieNameSpace.tokenExpired });
		StorageTime = Number(value) || 0;
	} else {
		StorageTime = Number(useCookie(CookieNameSpace.tokenExpired).value) || 0;
	}

	const hasToken =
		runtimeConfig.public.tokenStorage === 'preferenceStorage'
			? async () => {
					const { value } = await Preferences.get({ key: CookieNameSpace.getAccessToken });
					return !!value;
				}
			: () => !!useCookie(CookieNameSpace.getAccessToken).value;

	const tokenExists = runtimeConfig.public.tokenStorage === 'preferenceStorage' ? await hasToken() : hasToken();

	if (tokenExists && StorageTime - currentTime < CookieNameSpace.refreshTokenExpired) {
		return true;
	}
	return false;
};

export const getTokenFromStorage = async (key: string) => {
	const runtimeConfig = useRuntimeConfig();

	if (runtimeConfig.public.tokenStorage === 'preferenceStorage') {
		const { value } = await Preferences.get({ key });
		return value;
	}

	if (runtimeConfig.public.tokenStorage === 'localStorage') {
		return localStorage.getItem(key);
	}

	return useCookie(key).value;
};

export const removeTokenFromStorage = async (key: string) => {
	const runtimeConfig = useRuntimeConfig();

	const { clearAuthToken } = useAuthToken();
	clearAuthToken();

	if (runtimeConfig.public.tokenStorage === 'preferenceStorage') {
		await Preferences.remove({ key });
		await Preferences.remove({ key: CookieNameSpace.tokenExpired });
	} else if (runtimeConfig.public.tokenStorage === 'localStorage') {
		localStorage.removeItem(key);
	} else {
		useCookie(CookieNameSpace.tokenExpired).value = null;
		useCookie(key).value = null;
	}

	await removeCachedProfile();
	await clearOfflineRequests();
	await clearTruckerBackgroundCache();
};

// end token methods
